import React, { useState, useEffect } from "react";
import {
  Users,
  Search,
  Plus,
  Edit2,
  Trash2,
  ExternalLink,
  Linkedin,
  Twitter,
  Globe,
  MoreVertical,
} from "lucide-react";
import Modal from "../../components/Modal";
import { speakersAPI, adminAPI } from "../../services/api";

const SpeakerManager = () => {
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentSpeaker, setCurrentSpeaker] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    title: "",
    organization: "",
    bio: "",
    avatar_url: "",
    email: "",
    linkedin_url: "",
    twitter_url: "",
    website_url: "",
  });

  useEffect(() => {
    fetchSpeakers();
  }, []);

  const fetchSpeakers = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getSpeakers();
      setSpeakers(response.data);
    } catch (error) {
      console.error("Error fetching speakers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      title: "",
      organization: "",
      bio: "",
      avatar_url: "",
      email: "",
      linkedin_url: "",
      twitter_url: "",
      website_url: "",
    });
    setIsEditing(false);
    setCurrentSpeaker(null);
  };

  const handleAddClick = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEditClick = (speaker) => {
    setIsEditing(true);
    setCurrentSpeaker(speaker);
    setFormData({
      name: speaker.name || "",
      title: speaker.title || "",
      organization: speaker.organization || "",
      bio: speaker.bio || "",
      avatar_url: speaker.avatar_url || "",
      email: speaker.email || "",
      linkedin_url: speaker.linkedin_url || "",
      twitter_url: speaker.twitter_url || "",
      website_url: speaker.website_url || "",
    });
    setShowModal(true);
  };

  const handleDeleteClick = async (id) => {
    if (window.confirm("Are you sure you want to delete this speaker?")) {
      try {
        await adminAPI.deleteSpeaker(id);
        fetchSpeakers();
      } catch (error) {
        console.error("Error deleting speaker:", error);
        alert("Failed to delete speaker");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await adminAPI.updateSpeaker(currentSpeaker.id, formData);
      } else {
        await adminAPI.createSpeaker(formData);
      }
      setShowModal(false);
      resetForm();
      fetchSpeakers();
    } catch (error) {
      console.error("Error saving speaker:", error);
      alert("Failed to save speaker");
    }
  };

  const filteredSpeakers = speakers.filter(
    (s) =>
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.organization?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="admin-page">
      <header className="admin-section-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <div>
          <h1>Speaker Management</h1>
          <p>Manage conference speakers and presenters</p>
        </div>
        <button className="btn-admin btn-admin-primary" onClick={handleAddClick}>
          <Plus size={18} />
          Add Speaker
        </button>
      </header>

      <div className="admin-controls">
        <div className="search-bar">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search speakers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="admin-table-container">
        {loading ? (
          <div className="loading-spinner">Loading...</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Profile</th>
                <th>Name</th>
                <th>Title / Organization</th>
                <th>Contact</th>
                <th>Social</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSpeakers.map((speaker) => (
                <tr key={speaker.id}>
                  <td>
                    <img
                      src={
                        speaker.avatar_url || "https://via.placeholder.com/40"
                      }
                      alt={speaker.name}
                      className="avatar-sm"
                      onError={(e) =>
                        (e.target.src = "https://via.placeholder.com/40")
                      }
                    />
                  </td>
                  <td>
                    <span className="font-medium">{speaker.name}</span>
                  </td>
                  <td>
                    <div className="text-sm">{speaker.title}</div>
                    <div className="text-xs text-gray">
                      {speaker.organization}
                    </div>
                  </td>
                  <td>
                    <div className="text-sm">{speaker.email || "-"}</div>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      {speaker.linkedin_url && (
                        <a
                          href={speaker.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600"
                        >
                          <Linkedin size={14} />
                        </a>
                      )}
                      {speaker.twitter_url && (
                        <a
                          href={speaker.twitter_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400"
                        >
                          <Twitter size={14} />
                        </a>
                      )}
                      {speaker.website_url && (
                        <a
                          href={speaker.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-600"
                        >
                          <Globe size={14} />
                        </a>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        className="btn-icon"
                        onClick={() => handleEditClick(speaker)}
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        className="btn-icon delete"
                        onClick={() => handleDeleteClick(speaker.id)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={isEditing ? "Edit Speaker" : "Add Speaker"}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-section">
            <h4
              style={{
                marginBottom: "12px",
                borderBottom: "1px solid #eee",
                paddingBottom: "8px",
              }}
            >
              Professional Info
            </h4>
            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                name="name"
                className="form-control"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Job Title</label>
                <input
                  type="text"
                  name="title"
                  className="form-control"
                  value={formData.title}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Organization</label>
                <input
                  type="text"
                  name="organization"
                  className="form-control"
                  value={formData.organization}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Bio</label>
              <textarea
                name="bio"
                className="form-control"
                rows="3"
                value={formData.bio}
                onChange={handleInputChange}
              ></textarea>
            </div>
          </div>

          <div className="form-section" style={{ marginTop: "20px" }}>
            <h4
              style={{
                marginBottom: "12px",
                borderBottom: "1px solid #eee",
                paddingBottom: "8px",
              }}
            >
              Profile Image
            </h4>
            <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  overflow: "hidden",
                  backgroundColor: "#f0f0f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid #ddd",
                }}
              >
                {formData.avatar_url ? (
                  <img
                    src={formData.avatar_url}
                    alt="Preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onError={(e) => (e.target.style.display = "none")}
                  />
                ) : (
                  <Users size={24} color="#ccc" />
                )}
              </div>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label>Avatar URL</label>
                <input
                  type="url"
                  name="avatar_url"
                  className="form-control"
                  value={formData.avatar_url}
                  onChange={handleInputChange}
                  placeholder="https://..."
                />
              </div>
            </div>
          </div>

          <div className="form-section" style={{ marginTop: "20px" }}>
            <h4
              style={{
                marginBottom: "12px",
                borderBottom: "1px solid #eee",
                paddingBottom: "8px",
              }}
            >
              Contact & Social
            </h4>
            <div className="form-row">
              <div className="form-group">
                <label>Email (Optional)</label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Website URL</label>
                <input
                  type="url"
                  name="website_url"
                  className="form-control"
                  value={formData.website_url}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>LinkedIn URL</label>
                <input
                  type="url"
                  name="linkedin_url"
                  className="form-control"
                  value={formData.linkedin_url}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Twitter URL</label>
                <input
                  type="url"
                  name="twitter_url"
                  className="form-control"
                  value={formData.twitter_url}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-admin btn-admin-secondary"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn-admin btn-admin-primary">
              {isEditing ? "Update Speaker" : "Add Speaker"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SpeakerManager;
